<template>
  <div v-if="hasData" style="height: 100%">
    <StickyFooter :sticky="true" :grow-content="false">
      <template #header>
        <section class="hero is-success">
          <div class="hero-body p-4">
            <p class="title is-size-5">
              <span class="icon mr-4">
                <i class="fa-regular fa-laugh-beam"></i>
              </span>
              Dataset registration successful
            </p>
          </div>
        </section>
      </template>
      <!-- <template #body class="wowowow" style="color: white"> -->
      <template #body>
        <div class="notification-container is-stack align-items-center mt-2">
          <div class="notification is-info is-light">
            <span class="icon is-large">
              <i class="fa-solid fa-database fa-2x"></i>
            </span>
            <div>
              <p>You have registered</p>
              <ul>
                <li>
                  {{ datasetsCount }}
                  {{ pluralize('dataset', datasetsCount) }}
                  (associated to {{ samplesCount }}
                  {{ pluralize('sample', samplesCount) }})
                </li>
                <li>
                  {{ collectionsCount }}
                  {{ pluralize('collections', collectionsCount) }}
                </li>
              </ul>
            </div>
          </div>
          <div v-if="assay && assay.id" class="notification is-info is-light">
            <span class="icon is-large">
              <i class="fa-solid fa-thermometer-half fa-2x"></i>
            </span>
            <div class="text has-text-centered">
              This data will be associated to the {{ assay.type }} assay
              <router-link
                :to="{
                  name: 'assayDetail',
                  params: {
                    id: assay.id,
                    modelType: assay.type,
                  },
                }"
              >
                {{ assay.name }}
              </router-link>
            </div>
          </div>
          <div class="notification is-info is-light">
            <span class="icon is-large mr-4">
              <i class="fa-solid fa-hourglass-half fa-2x"></i>
            </span>
            <div class="text has-text-centered">
              <p>
                The data is currently being moved from your dropbox to a
                read-only location managed by {{ brandShort }}.
              </p>
              <p>
                An asynchronous task has been created to proceed with your
                dataset registration.
              </p>
              <p>
                The progress can be followed on the
                <router-link :to="{ name: 'userTasks' }">task list</router-link>
              </p>
              <p>
                You will receive an
                <span class="icon is-small">
                  <i class="fa-regular fa-envelope fa-xs" />
                </span>
                email when the process has successfully completed
              </p>
            </div>
          </div>
        </div>
      </template>
      <!-- </template> -->
      <template #footer>
        <div class="is-flex">
          <router-link :to="{ name: 'userTasks' }">
            <BaseButton
              class="mr-2"
              variant="primary"
              size="small"
              :outlined="true"
              :icon="['fa', 'fa-tasks']"
              label="Go to Tasklist"
            />
          </router-link>
          <router-link
            v-if="assay && assay.id"
            :to="{
              name: 'assayDetail',
              params: { id: assay.id, modelType: assay.type },
            }"
          >
            <BaseButton
              class="mr-2"
              variant="primary"
              size="small"
              :outlined="true"
              :icon="['fa', 'fa-thermometer-half']"
              label="Go to Assay"
            />
          </router-link>
          <router-link :to="{ name: 'welcome' }">
            <BaseButton variant="primary" size="small" label="Leave" />
          </router-link>
        </div>
      </template>
    </StickyFooter>

    <!-- Might consider writing an <Alert /> component  -->
    <ModalCard
      v-if="isOpenModal"
      :title="`
          <span class='icon is-small mr-1'>
            <i class='fa-solid fa-triangle-exclamation fa-xs'></i>
          </span>
          <span>Warning</span>
        `"
      :handle-close="() => (isOpenModal = false)"
    >
      <template #body>
        <div>
          <slot name="modal-body">
            <p>
              Data must not be removed or modified until the transfer is
              successfully confirmed by email
            </p>
          </slot>
        </div>
      </template>
      <template #footer>
        <div
          class="is-flex is-justify-content-space-between"
          style="width: 100%"
        >
          <slot name="modal-footer">
            <button
              class="button is-primary is-small is-outlined"
              @click="isOpenModal = false"
            >
              Ok, I Understood!
            </button>
          </slot>
        </div>
      </template>
    </ModalCard>
  </div>
</template>

<script>
import pluralize from 'pluralize'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { brandShort } from '@/../app.config'
import { usePageShell } from '@/composables/usePageShell'
import { useStore } from '@/store'

import StickyFooter from '@/components/data-loader/StickyFooter.vue'

export default {
  name: 'LoadingSuccessfulView',
  components: {
    StickyFooter,
  },
  setup() {
    usePageShell({ title: 'Dataset registration successful' })
    const store = useStore()
    const router = useRouter()
    const successData = computed(() => store.state.dataloader.main.successData)

    // Redirect if no data (hard reload)
    const hasData = !!successData.value
    if (!hasData) {
      router.replace({ name: 'welcome' })
    }

    const assay = computed(() => (successData.value?.assays || [])[0] || {})
    const datasets = computed(() => successData.value?.datasets || [])
    const samples = computed(() => successData.value?.samples || [])
    const collections = computed(() => successData.value?.collections || [])
    const datasetsCount = computed(() => datasets.value.length)
    const samplesCount = computed(() => samples.value.length)
    const collectionsCount = computed(() => collections.value.length)

    return {
      hasData,
      brandShort,
      assay,
      datasetsCount,
      samplesCount,
      collectionsCount,
      pluralize,
    }
  },
  data() {
    return {
      isOpenModal: true,
    }
  },
}
</script>
